import {
  apiClient,
  APIResponse,
  API_ORIGIN,
  authStorage,
  clearCsrfToken,
  ensureCsrfCookie,
  extractApiError,
} from './api';

async function refreshAuthCookies() {
  clearCsrfToken();
  await ensureCsrfCookie(true);
}

export const authAPI = {
  async bootstrap() {
    await ensureCsrfCookie();
    return APIResponse.success(null);
  },

  async register({ email, password1, password2 }) {
    try {
      const { data } = await apiClient.post('/auth/register/', {
        email,
        password1,
        password2,
      });
      await refreshAuthCookies();
      if (data?.user) {
        authStorage.write({ user: data.user });
      }
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(extractApiError(error, 'Não foi possível criar a conta.'));
    }
  },

  async login({ email, password }) {
    try {
      const { data } = await apiClient.post('/auth/login/', { email, password });
      await refreshAuthCookies();
      if (data?.user) {
        authStorage.write({ user: data.user });
      }
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(extractApiError(error, 'Credenciais inválidas.'));
    }
  },

  async logout() {
    try {
      await apiClient.post('/auth/logout/', {});
    } catch (_error) {
      // Best-effort: clear local state even if server rejects.
    }
    authStorage.clear();
    clearCsrfToken();
    return APIResponse.success(null);
  },

  async fetchMe() {
    try {
      const { data } = await apiClient.get('/me/');
      authStorage.write({ user: data });
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(extractApiError(error, 'Não foi possível obter o utilizador.'));
    }
  },

  async patchProfile(profileFields) {
    try {
      const { data } = await apiClient.patch('/me/', profileFields);
      authStorage.write({ user: data });
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(extractApiError(error, 'Não foi possível guardar o perfil.'));
    }
  },

  async saveOnboardingStep(step, completed = false) {
    try {
      const { data } = await apiClient.post('/onboarding/step/', { step, completed });
      authStorage.write({ user: data });
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(extractApiError(error, 'Não foi possível guardar o passo.'));
    }
  },

  async requestPasswordReset(email) {
    try {
      await apiClient.post('/auth/password/reset/', { email });
      return APIResponse.success(null);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível enviar o email de recuperação.'),
      );
    }
  },

  async confirmPasswordReset({ uid, token, new_password1, new_password2 }) {
    try {
      await apiClient.post('/auth/password/reset/confirm/', {
        uid,
        token,
        new_password1,
        new_password2,
      });
      return APIResponse.success(null);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível redefinir a password.'),
      );
    }
  },

  googleLoginUrl(process = 'login') {
    return `${API_ORIGIN}/accounts/google/login/?process=${process}`;
  },
};

export { API_ORIGIN };

export const guidedFlowAPI = {
  async get() {
    try {
      const { data } = await apiClient.get('/profile/guided-flow/');
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível carregar o fluxo guiado.'),
      );
    }
  },

  async advance() {
    try {
      const { data } = await apiClient.post('/profile/guided-flow/advance/');
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível avançar o fluxo guiado.'),
      );
    }
  },

  async skip() {
    try {
      const { data } = await apiClient.post('/profile/guided-flow/skip/');
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível saltar este passo.'),
      );
    }
  },

  async markWizardSeen(name) {
    try {
      const { data } = await apiClient.post('/profile/wizards/seen/', { name });
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível marcar o wizard como visto.'),
      );
    }
  },
};

export const visionWidgetAPI = {
  async get(force = false) {
    try {
      const url = force
        ? '/dashboard/vision-widget/?force=1'
        : '/dashboard/vision-widget/';
      const { data } = await apiClient.get(url);
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível carregar o widget.'),
      );
    }
  },
  async refresh() {
    try {
      const { data } = await apiClient.post('/dashboard/vision-widget/');
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível actualizar o widget.'),
      );
    }
  },
};
